import type { Role } from '../api/types'

export type IconName =
  | 'dashboard' | 'assets' | 'inspections' | 'workorders' | 'findings' | 'lubrication' | 'measurements' | 'alerts'
  | 'quality' | 'imports' | 'exports' | 'templates' | 'users' | 'clients' | 'more' | 'bell' | 'scan'

export interface NavItem {
  key: string
  label: string
  path: string
  icon: IconName
  roles?: Role[]
}

const STAFF: Role[] = ['admin', 'engineer']
const WRITERS: Role[] = ['admin', 'engineer', 'inspector']

/** Only these pages appear in the navigation; the rest stay reachable by URL. */
export const NAV_ITEMS: NavItem[] = [
  { key: 'dashboard', label: 'Panel', path: '/', icon: 'dashboard' },
  { key: 'assets', label: 'Activos', path: '/assets', icon: 'assets' },
  { key: 'inspections', label: 'Inspecciones', path: '/inspections', icon: 'inspections' },
  { key: 'clients', label: 'Clientes', path: '/clients', icon: 'clients', roles: STAFF },
  { key: 'users', label: 'Usuarios', path: '/users', icon: 'users', roles: ['admin'] },
]

export interface Navigation {
  /** Tabs of the top pill navigation. */
  primary: NavItem[]
  /** Everything else, in the "More" menu. */
  more: NavItem[]
  /** The bottom bar on phones (the rest goes to the drawer). */
  mobile: NavItem[]
}

export function navFor(role: Role | undefined): Navigation {
  const allowed = NAV_ITEMS.filter((i) => !i.roles || (role !== undefined && i.roles.includes(role)))
  const primary = allowed
  const more: NavItem[] = []
  const mobileKeys = role === 'inspector' ? ['assets', 'inspections', 'dashboard'] : ['dashboard', 'assets', 'inspections', 'clients']
  const mobile = mobileKeys.map((k) => allowed.find((i) => i.key === k)).filter((i): i is NavItem => Boolean(i))
  return { primary, more, mobile }
}

const CAPABILITIES = {
  'inspection.create': WRITERS,
  'measurement.create': WRITERS,
  'lubrication.record': WRITERS,
  'lubrication.spec': STAFF,
  'finding.create': WRITERS,
  'finding.edit': STAFF,
  'workorder.create': STAFF,
  'workorder.transition': WRITERS,
  'import.upload': STAFF,
  'import.commit': STAFF,
  'quality.resolve': STAFF,
  'alert.acknowledge': WRITERS,
  'alert.resolve': STAFF,
  'alert.evaluate': STAFF,
  'tags.manage': STAFF,
  'template.edit': STAFF,
  'user.manage': ['admin'],
  'export.download': ['admin', 'engineer', 'inspector', 'viewer'],
  'equipment.edit': STAFF,
  'equipment.create': STAFF,
  'equipment.documents': STAFF,
  'client.manage': STAFF,
} as const satisfies Record<string, Role[]>

export type Capability = keyof typeof CAPABILITIES

export function can(role: Role | undefined, capability: Capability): boolean {
  if (!role) return false
  const roles = (CAPABILITIES as Record<string, readonly Role[]>)[capability]
  return roles ? roles.includes(role) : false
}

const PATH_RULES: { test: RegExp; capability?: Capability; roles?: Role[] }[] = [
  { test: /^\/imports(\/|$)/, roles: STAFF },
  { test: /^\/users(\/|$)/, roles: ['admin'] },
  { test: /^\/clients(\/|$)/, roles: STAFF },
  { test: /^\/e\/[^/]+\/(inspect|measure|lubricate)$/, roles: WRITERS },
  { test: /^\/inspections\/new(\/|$)/, roles: WRITERS },
]

export function canAccessPath(role: Role | undefined, path: string): boolean {
  if (!role) return false
  const rule = PATH_RULES.find((r) => r.test.test(path))
  if (!rule) return true
  return rule.roles ? rule.roles.includes(role) : true
}

export function homePath(_role: Role | undefined): string {
  return '/'
}
